import {
  Button,
  Divider,
  FormControl,
  Input,
  InputGroup,
  InputRightAddon,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslations } from 'use-intl';

import { ErrorMessage, ResponsiveModal, ResponsiveModalFooter } from '@blockpulse3/ui/commons';

import { schemaPreferredShares } from '../../provider';
import { ISharePercentageForm } from './types';

type Props = {
  isOpen: boolean;
  defaultValues: ISharePercentageForm;
  onSubmit: (data: ISharePercentageForm) => void;
  onClose: () => void;
};

/**
 * UpdatePreferredSharesPercentageModal.
 * Legal version of the ShareholderForm.
 *
 * @param {Props}
 * @returns {JSX.Element}
 */
export function UpdatePreferredSharesPercentageModal({
  isOpen,
  onClose,
  defaultValues,
  onSubmit,
}: Props): JSX.Element {
  const i18nLegalShareholderInformationsForm = useTranslations(
    'UpdatePreferredSharesPercentageModal',
  );

  const { register, formState, handleSubmit } = useForm<ISharePercentageForm>({
    defaultValues,
    resolver: yupResolver(schemaPreferredShares),
  });

  const handleFormSubmit: SubmitHandler<ISharePercentageForm> = (data) => {
    onSubmit(data);
  };

  return (
    <ResponsiveModal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{i18nLegalShareholderInformationsForm('title')}</ModalHeader>
        <ModalBody>
          <form id="share-preference" onSubmit={handleSubmit(handleFormSubmit)}>
            <FormControl isInvalid={!!formState.errors.percentage}>
              <InputGroup>
                <Input
                  id="preferredSharesPercentages"
                  step="0.01"
                  type="number"
                  {...register('percentage')}
                />
                <InputRightAddon>%</InputRightAddon>
              </InputGroup>
              <ErrorMessage error={formState.errors.percentage} />
            </FormControl>
          </form>
        </ModalBody>
        <Divider />
        <ResponsiveModalFooter>
          <Button type="button" variant="secondary" onClick={onClose}>
            {i18nLegalShareholderInformationsForm('Cancel')}
          </Button>
          <Button form="share-preference" type="submit">
            {i18nLegalShareholderInformationsForm('Validate')}
          </Button>
        </ResponsiveModalFooter>
      </ModalContent>
    </ResponsiveModal>
  );
}

export type UpdatePreferredSharesPercentageModalProps = Props;
